import { useT, useUiStore } from '@/store';
import { usePendingRequestCount } from '@/hooks/useClientRequests';

/**
 * Top-level tabs — the monolith's full set, in its original order, minus one.
 *
 * The Driver App tab is deliberately absent: it was a phone-shaped <div>
 * inside the dispatcher console, and it is now a standalone mobile bundle at
 * /driver.html (different device, different auth path, different bundle). It
 * is reachable from the header link rather than pretending to be a peer tab
 * of the console's own views.
 */
const TABS = [
  { id: 'dispatcher', labelKey: 'nav.dispatcher' },
  { id: 'command', labelKey: 'nav.command' },
  { id: 'approvals', labelKey: 'nav.approvals', badge: 'pendingRequests' },
  { id: 'client', labelKey: 'nav.clientPortal' },
  { id: 'data', labelKey: 'nav.quarriesClients' },
  { id: 'drivers', labelKey: 'nav.drivers' },
  { id: 'leaves', labelKey: 'nav.leaves' },
  { id: 'orders', labelKey: 'nav.orders' },
  // Between the orders it bills and the analytics that report on them —
  // which is also the order the work happens in.
  { id: 'billing', labelKey: 'nav.billing' },
  { id: 'analytics', labelKey: 'nav.analytics' },
  { id: 'fleet', labelKey: 'nav.transportas' },
  { id: 'telegram', labelKey: 'nav.telegram' },
  // Last, deliberately: company details, tariffs, logins and the API key
  // are read once a year at most, the same reasoning that used to keep them
  // at the bottom of the "Karjerai / Klientai" tab (see AdminView.jsx).
  { id: 'admin', labelKey: 'nav.admin' },
];

export function Navigation() {
  const t = useT();
  const view = useUiStore((s) => s.view);
  const setView = useUiStore((s) => s.setView);
  // Shares the Approvals view's own query cache entry, so the badge costs no
  // extra request and updates from the same SSE event the view does.
  const pendingRequests = usePendingRequestCount();

  return (
    <nav className="flex gap-1.5 md:flex-wrap" aria-label="Views">
      {TABS.map((tab) => {
        const count = tab.badge === 'pendingRequests' ? pendingRequests : 0;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => setView(tab.id)}
            aria-current={view === tab.id ? 'page' : undefined}
            className={`nav-pill ${view === tab.id ? 'nav-pill-active' : ''}`}
          >
            {t(tab.labelKey)}
            {count > 0 ? (
              <span
                className="ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-amber/20 px-1.5 py-0.5 text-[10px] font-bold text-amber"
                // Announced as a labelled count rather than a bare number,
                // which a screen reader would otherwise read as part of the
                // tab name ("Approvals 3").
                aria-label={t('nav.approvalsPending', { n: count })}
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}
